import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import type { ConfirmationResult } from 'firebase/auth'
import { signUp, startPhoneLink, confirmPhoneLink } from '../../lib/firebase'
import { meApi, kycApi, supplierDataApi, orgApi, unrecognizedKeysFrom, type OrgMatch, type SignupProfileBody } from '../../api/endpoints'
import { ApiError } from '../../api/types'
import { PressScale } from '../../lib/PressScale'
import { feedback } from '../../lib/feedback'
import { PasswordInput } from '../PasswordInput'
import { OrgCrest } from '../org/OrgCrest'
import { AddressStep, type WizardAddress } from './AddressStep'
import { StepDocs, type UploadedDoc } from './StepDocs'
import { StepErrorBoundary } from './StepErrorBoundary'
import {
  DataSourceSetup, EMPTY_DRAFT, draftToSaveBody, draftIsSubmittable,
  type DataSourceDraft,
} from '../supplier/DataSourceSetup'

/**
 * /signup — the typeform-style onboarding wizard (2026-08 redesign;
 * org fork + join branch 2026-08-11).
 *
 * One question per screen, Enter to advance. The fork is TWO DOORS —
 *   an organization (a company that trades)  → capabilities, company details,
 *                                              proof of company
 *   an individual broker                     → references or an affiliation
 * — and "supplier vs buyer" is no longer a door, it is a CAPABILITY: an org
 * ticks buy, sell, or both. `accountType` on the wire is unchanged (sell ⇒
 * 'supplier'), and the new `tradeRole` carries the one fact that mapping
 * cannot express, `'both'`.
 *
 * ── THE JOIN BRANCH ───────────────────────────────────────────────────────
 * Right after the account exists we ask the server one question: is the
 * company this person just named already on DiaEx, and does their VERIFIED
 * email domain say they work there? If so, they are offered a request to join
 * it and INHERIT its verification instead of founding a duplicate org. The
 * domain is derived server-side from the Auth record — the client never sends
 * it — so this cannot be used to ask "is company X on DiaEx".
 *
 * `GET /org/discover` 404s entirely while ORGS_LIVE is off, and the client
 * swallows every failure to "no match", so with the flag off the step does
 * not exist and signup behaves exactly as it did.
 *
 * ── NO DEAD ENDS ──────────────────────────────────────────────────────────
 * A join request is a SIDE EFFECT; the wizard always continues and the user
 * always finishes with their own working account. "This is a different
 * company" is always on screen, the request is undoable, and a failed request
 * offers "Try again" beside "Continue without it".
 *
 * The Firebase account is created mid-flow (credentials step) — T&C consent
 * is captured on that same screen, BEFORE the account exists. Everything
 * after runs authed: phone OTP linking, KYC doc upload via signed URLs, and
 * two /me/signup-profile saves (partial right after create, full at finish).
 *
 * Shared verbatim by the desktop route (/signup) and the mobile shell — the
 * layout is one centered column that already reads as an app screen, and it
 * is designed at 320px first.
 */

type AccountType = 'supplier' | 'buyer' | 'broker'
type StepId =
  | 'type' | 'caps' | 'name' | 'company' | 'credentials' | 'team' | 'phone'
  | 'website' | 'address' | 'hearAbout' | 'docs' | 'refs' | 'stock' | 'done'

interface BrokerRef { name: string; company: string; relationship: string; email: string; phone: string }

/** An extra company phone line collected at signup, beyond the verified work
 *  mobile (the business-WhatsApp designate) and the office/company phone.
 *  `number` is typed free-form — a '+'-prefixed entry is taken as-is, otherwise
 *  the work-mobile dial code applies (same rule as the company-phone field). */
interface ExtraPhone { number: string; label: string }

/** What an org may do here. BOTH may be true — that is `tradeRole:'both'`,
 *  which was unreachable before this fork existed. */
interface Capabilities { buy: boolean; sell: boolean }

/** The offer state for "your team is already here". One-shot: once answered
 *  it is never re-shown, including after a reload. */
type TeamOffer = 'none' | 'shown' | 'requested' | 'dismissed'

interface WizardState {
  /** ORGANIZATION door → 'buyer' | 'supplier' (derived from `caps`);
   *  BROKER door → 'broker'. Unchanged on the wire. */
  accountType: AccountType | null
  caps: Capabilities
  contactName: string
  companyName: string
  email: string
  agreed: boolean
  accountCreated: boolean
  dialCode: string
  phone: string
  companyPhone: string
  /** Extra company lines beyond work-mobile + company-phone — persisted into
   *  the structured `contacts` block server-side. The work mobile stays the
   *  single business-WhatsApp designate. */
  additionalPhones: ExtraPhone[]
  messagingOptIn: boolean
  phoneVerified: boolean
  website: string
  address: WizardAddress | null
  hearAbout: string
  hearAboutOther: string
  docs: UploadedDoc[]
  refs: BrokerRef[]
  useAffiliation: boolean
  affiliation: string
  teamOffer: TeamOffer
  /** The orgs discovery offered. Names only — the server sends no member
   *  count, no verification state and no profile (that is deliberate). */
  teamOrgs: OrgMatch[]
  /** WHICH of them they asked. Persisted so a reload — or a Back — renders
   *  the receipt for the right company instead of re-offering the list. */
  teamRequestOrgId: string | null
  /** Verification submitted — only the done screen remains. */
  finished: boolean
}

const EMPTY_REF: BrokerRef = { name: '', company: '', relationship: '', email: '', phone: '' }

const INITIAL: WizardState = {
  accountType: null,
  // `buy` is pre-checked: every org buys, selling is the addition. This is
  // what makes the capabilities step structurally incapable of erroring.
  caps: { buy: true, sell: false },
  contactName: '',
  companyName: '',
  email: '',
  agreed: false,
  accountCreated: false,
  dialCode: '+1',
  phone: '',
  companyPhone: '',
  additionalPhones: [],
  messagingOptIn: false,
  phoneVerified: false,
  website: '',
  address: null,
  hearAbout: '',
  hearAboutOther: '',
  docs: [],
  refs: [{ ...EMPTY_REF }],
  useAffiliation: false,
  affiliation: '',
  teamOffer: 'none',
  teamOrgs: [],
  teamRequestOrgId: null,
  finished: false,
}

/** Which door an accountType came through. */
const isOrgType = (t: AccountType | null): boolean => t === 'supplier' || t === 'buyer'

/** caps → the accountType the server already understands. Sell (with or
 *  without buy) is a supplier account; buy alone is a buyer account. */
const typeForCaps = (c: Capabilities): AccountType => (c.sell ? 'supplier' : 'buyer')

/** caps → the org's `tradeRole`. The ONLY producer of `'both'`. */
function tradeRoleFor(accountType: AccountType | null, caps: Capabilities): 'buyer' | 'supplier' | 'both' {
  if (accountType === 'broker') return 'buyer'
  if (caps.buy && caps.sell) return 'both'
  return caps.sell ? 'supplier' : 'buyer'
}

/** True while a signup is in flight — the shells use this to keep the auth
 *  gate from bouncing a freshly-created (now signed-in) user out mid-wizard.
 *
 *  "In flight" means AN ACCOUNT ALREADY EXISTS and the wizard has not finished.
 *  It deliberately does NOT mean "the wizard is mounted".
 *
 *  This used to be `!!sessionStorage.getItem(STORAGE_KEY)`, and the persist
 *  effect writes that key on every state change — including the first render.
 *  So merely LANDING on /signup latched it true for the rest of the browser
 *  session, since the key is only removed on the two finish paths. Both
 *  consequences were real:
 *    - InviteWatcher bails on signupWizardInFlight(), so an emailed invite link
 *      silently did nothing for any signed-in user who had opened /signup in
 *      that session.
 *    - routes/Signup.tsx stopped redirecting an already-signed-in visitor away
 *      from the wizard.
 *  Keying on `accountCreated` makes the predicate mean what its name says: if no
 *  account was created there is nothing for an auth gate to bounce, because an
 *  anonymous visitor is not subject to that gate at all. */
export function signupWizardInFlight(): boolean {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return false
    const saved = JSON.parse(raw) as { accountCreated?: unknown } | null
    return saved?.accountCreated === true
  } catch { return false }
}

// Resume support: a reload mid-wizard (especially after the account exists)
// must not strand the user at step one. Password is never persisted.
const STORAGE_KEY = 'diaex-signup-wizard'

function loadSaved(): WizardState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const saved = JSON.parse(raw) as Partial<WizardState>
    const merged = { ...INITIAL, ...saved }
    // A state written by the PREVIOUS wizard (or a hand-edited one) has no
    // `caps`, and `state.caps.sell` is read on the render path — a spread
    // that leaves it undefined is a white screen, not a missing feature.
    return {
      ...merged,
      caps: {
        buy: merged.caps?.buy !== false,
        sell: merged.caps?.sell === true,
      },
      teamOrgs: Array.isArray(merged.teamOrgs) ? merged.teamOrgs : [],
      docs: Array.isArray(merged.docs) ? merged.docs : [],
      refs: Array.isArray(merged.refs) && merged.refs.length ? merged.refs : [{ ...EMPTY_REF }],
    }
  } catch { return null }
}

/** The state a `?type=` deep link starts from. `?type=supplier` (every
 *  "Become a supplier" CTA) preselects the ORGANIZATION door with `sell`
 *  already ticked — and lands on the capabilities step, editable, with Back
 *  still reaching the door. A supplier CTA must never trap an actual buyer. */
function stateForInitialType(t?: AccountType): WizardState {
  if (t === 'supplier') return { ...INITIAL, accountType: 'supplier', caps: { buy: true, sell: true } }
  if (t === 'buyer') return { ...INITIAL, accountType: 'buyer', caps: { buy: true, sell: false } }
  if (t === 'broker') return { ...INITIAL, accountType: 'broker' }
  return { ...INITIAL }
}

// Diamond trade centers first — the audience really is this concentrated.
const DIAL_CODES = [
  { code: '+1', label: 'US/CA' },
  { code: '+91', label: 'India' },
  { code: '+971', label: 'UAE' },
  { code: '+32', label: 'Belgium' },
  { code: '+972', label: 'Israel' },
  { code: '+852', label: 'Hong Kong' },
  { code: '+44', label: 'UK' },
  { code: '+65', label: 'Singapore' },
  { code: '+66', label: 'Thailand' },
  { code: '+81', label: 'Japan' },
  { code: '+61', label: 'Australia' },
  { code: '+27', label: 'South Africa' },
]

const HEAR_OPTIONS = [
  'Google search', 'LinkedIn', 'Instagram', 'Trade show', 'WhatsApp / trade group',
  'Referred by a colleague', 'Aska / Heera network', 'Other',
]

// ─── Facet-line icons (48px, stroke 1.6 — same vocabulary as the app glyphs) ──
//
// `size` exists because these glyphs now live in two places at two scales: the
// 48px door / capability tile, and the 28px identity slot in the sheet header
// that the chosen one FLIES INTO. One drawing, two sizes, no second asset.

interface GlyphProps { active?: boolean; size?: number }

function IconSupplier({ active = false, size = 48 }: GlyphProps) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" aria-hidden>
      {/* rough octahedron → light rays: the source of stones */}
      <path d="M14 8h20l8 11-18 21L6 19l8-11Z" className="stroke-current" />
      <path d="M6 19h36" className={active ? 'stroke-gold-500' : 'stroke-current opacity-40'} />
      <path d="M14 8l5 11 5 21 5-21 5-11" className="stroke-current opacity-60" />
      <path d="M24 2v3M38 4l-1.8 2.4M10 4l1.8 2.4" className={active ? 'stroke-gold-500' : 'stroke-current opacity-40'} />
    </svg>
  )
}

function IconBuyer({ active = false, size = 48 }: GlyphProps) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" aria-hidden>
      {/* jeweler's loupe over a stone: the business that sources */}
      <circle cx="21" cy="21" r="13" className="stroke-current" />
      <path d="M30.5 30.5 42 42" className="stroke-current" />
      <path d="M15 18.5h12l-6 9-6-9Z" className="stroke-current opacity-70" />
      <path d="M15 18.5h12" className={active ? 'stroke-gold-500' : 'stroke-current opacity-40'} />
      <path d="M17.5 15.5l2 3M28.5 15.5l-2 3" className="stroke-current opacity-50" />
    </svg>
  )
}

function IconBroker({ active = false, size = 48 }: GlyphProps) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" aria-hidden>
      {/* two stones joined by an arc: the connector */}
      <path d="M9 22h12l-6 8.5L9 22Z" className="stroke-current" />
      <path d="M27 22h12l-6 8.5L27 22Z" className="stroke-current" />
      <path d="M9 22h12M27 22h12" className={active ? 'stroke-gold-500' : 'stroke-current opacity-40'} />
      <path d="M15 17c2.5-6 15.5-6 18 0" className="stroke-current opacity-60" strokeDasharray={active ? undefined : '2.5 3'} />
    </svg>
  )
}

/** The ONE new glyph this fork needs: a table-cut stone on a two-step plinth,
 *  flanked by two pips. A company is a structure AND its people — which is
 *  also, precisely, what a seat is. */
function IconOrganization({ active = false, size = 48 }: GlyphProps) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" aria-hidden>
      {/* table-cut stone */}
      <path d="M16 6h16l6 8-14 15L8 14l8-8Z" className="stroke-current" />
      <path d="M8 14h30" className="stroke-current opacity-45" />
      <path d="M16 6l4 8 3 15 3-15 4-8" className="stroke-current opacity-55" />
      {/* the plinth: two steps, the top edge lit when chosen */}
      <path d="M12 34h24" className={active ? 'stroke-gold-500' : 'stroke-current opacity-45'} />
      <path d="M7 41h34" className="stroke-current opacity-70" />
      {/* its people */}
      <circle cx="6" cy="27" r="2.6" className={active ? 'stroke-gold-500' : 'stroke-current opacity-45'} />
      <circle cx="42" cy="27" r="2.6" className={active ? 'stroke-gold-500' : 'stroke-current opacity-45'} />
    </svg>
  )
}

type DoorId = 'org' | 'broker'

const DOOR_CARDS: Array<{ door: DoorId; title: string; blurb: string; Icon: typeof IconSupplier }> = [
  {
    door: 'org',
    title: 'An organization',
    // The ONLY place in the product where inherited verification is said out
    // loud to the person it benefits, before they are ever asked to pay for
    // it. Do not trim this to fit.
    blurb: 'A company that buys or sells stones. Your team joins on your seats, and inherits your verification.',
    Icon: IconOrganization,
  },
  {
    door: 'broker',
    title: 'An individual broker',
    blurb: 'You trade on your own name. Verified through references, not company papers.',
    Icon: IconBroker,
  },
]

const CAP_ROWS: Array<{ key: keyof Capabilities; title: string; hint: string; Icon: typeof IconSupplier }> = [
  { key: 'buy', title: 'Buy stones', hint: 'See prices and contact suppliers direct.', Icon: IconBuyer },
  // "Adds one step" is the whole visible-debt mechanic: it is TRUE (the stock
  // step is appended when this is on), it is one line, and it survives 320px.
  { key: 'sell', title: 'Sell / list stock', hint: 'Adds one step: how you’ll send us inventory.', Icon: IconSupplier },
]

/** Announced to assistive tech on every step change — the visible "4 / 11"
 *  fraction is silent to a screen reader once the region has been read. */
const STEP_TITLES: Record<StepId, string> = {
  type: 'Who is signing up',
  caps: 'What you will do here',
  name: 'Your name',
  company: 'Your company name',
  credentials: 'Create your login',
  team: 'Your team may already be here',
  phone: 'Your work phone',
  website: 'Your website',
  address: 'Your address',
  hearAbout: 'How you heard about DiaEx',
  docs: 'Your company papers',
  refs: 'Trade references',
  stock: 'How you will send us stock',
  done: 'Done',
}

/** The identity glyph for a chosen door — the thing that flies into the
 *  header slot and stays there for the rest of the wizard. */
function DoorGlyph({ type, size = 28 }: { type: AccountType | null; size?: number }) {
  if (type === 'broker') return <IconBroker size={size} active />
  if (!type) return null
  return <IconOrganization size={size} active />
}

// ─── The wizard ─────────────────────────────────────────────────────────────

export function SignupWizard({ onDone, onSignIn, initialType }: {
  onDone: () => void
  onSignIn?: () => void
  /** Preselect the account type (e.g. /signup?type=supplier from a
   *  "Become a supplier" CTA) — the type step is skipped. A saved
   *  in-flight wizard always wins over the param. */
  initialType?: AccountType
}) {
  const reduce = useReducedMotion() ?? false
  const navigate = useNavigate()
  const [state, setState] = useState<WizardState>(() => loadSaved() ?? stateForInitialType(initialType))
  const [password, setPassword] = useState('')
  // Like the password, the stock-connect draft holds a secret (the feed API
  // key) — it lives OUTSIDE the sessionStorage-persisted WizardState.
  const [stockDraft, setStockDraft] = useState<DataSourceDraft>(EMPTY_DRAFT)
  const [stepIndex, setStepIndex] = useState(() =>
    loadSaved()?.accountCreated ? -1 : !loadSaved() && initialType ? 1 : 0) // -1 → resolve after steps compute
  const [direction, setDirection] = useState(1)
  const [error, setError] = useState<string | null>(null)
  /**
   * A recovery the error itself offers. "An account with that email already
   * exists — sign in instead?" shipped as PROSE with nothing clickable: the
   * one thing the sentence tells you to do, you could not do. Every error that
   * names a way out now carries the control that performs it.
   */
  const [errorAction, setErrorAction] = useState<{ label: string; run: () => void; testId: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const patch = useCallback((p: Partial<WizardState>) => setState((s) => ({ ...s, ...p })), [])
  /**
   * Documents get their own FUNCTIONAL setter. The docket invites several
   * uploads in parallel (that is the point of one screen rather than five),
   * and `patch({ docs: [...state.docs, doc] })` reads `state` from the
   * closure the upload STARTED in — so two files landing near-simultaneously
   * would silently drop one.
   */
  const patchDocs = useCallback(
    (update: (prev: UploadedDoc[]) => UploadedDoc[]) => setState((s) => ({ ...s, docs: update(s.docs) })),
    [],
  )
  const fail = useCallback((message: string | null, action?: { label: string; run: () => void; testId: string }) => {
    setError(message)
    // A new message always replaces the previous recovery — a stale "Sign in
    // instead" under an unrelated error is worse than no button at all.
    setErrorAction(message ? (action ?? null) : null)
  }, [])

  // Persist answers (never the password).
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)) } catch { /* private mode */ }
  }, [state])

  const isOrg = isOrgType(state.accountType)
  /** Before a door is picked, the org-only steps stay in the count so the
   *  "3 / 9" fraction doesn't lurch the moment they choose. */
  const orgish = isOrg || state.accountType === null

  const steps = useMemo<StepId[]>(() => {
    const base: StepId[] = ['type']
    if (orgish) base.push('caps')
    base.push('name')
    if (orgish) base.push('company')
    base.push('credentials')
    // Inserted when discovery came back with a company, and then it STAYS —
    // answering must not remove the step you are standing on. (It did, once:
    // dismissing shortened the list under the current index, so `go(1)`
    // landed a step further along and the phone step was silently skipped.)
    // The insert happens before the advance out of `credentials`, so the step
    // is already in the list when we land on it — no card popping in late.
    if (state.teamOffer !== 'none') base.push('team')
    base.push('phone')
    if (orgish) base.push('website')
    base.push('address', 'hearAbout')
    base.push(state.accountType === 'broker' ? 'refs' : 'docs')
    // One more for anyone who SELLS: how they'll feed us stock (skippable —
    // the same setup lives on My Stock after approval).
    if (isOrg && state.caps.sell) base.push('stock')
    base.push('done')
    return base
  }, [orgish, isOrg, state.accountType, state.caps.sell, state.teamOffer])


  // Resume: land on the first step that still needs input.
  useEffect(() => {
    if (stepIndex !== -1) return
    const s = state
    const target: StepId =
      s.finished ? 'done'
      // Only 'shown' resumes here — an answered offer is never re-asked.
      : s.teamOffer === 'shown' ? 'team'
      : !s.phone && !s.phoneVerified ? 'phone'
      : isOrg && !s.website && !s.address ? 'website'
      : !s.address ? 'address'
      : !s.hearAbout ? 'hearAbout'
      : s.accountType === 'broker' ? 'refs'
      : !s.docs.length ? 'docs'
      // `stock` had NO resume entry: a seller who reloaded on it was thrown
      // back to the docs step with every document already uploaded.
      : s.caps.sell ? 'stock'
      : 'docs'
    setStepIndex(Math.max(0, steps.indexOf(target)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, steps])

  const step = steps[Math.max(0, stepIndex)] ?? 'type'
  const progress = Math.min(1, Math.max(0, stepIndex) / (steps.length - 1))

  const go = useCallback((delta: number) => {
    setError(null)
    setErrorAction(null)
    setDirection(delta)
    setStepIndex((i) => Math.min(steps.length - 1, Math.max(0, i + delta)))
  }, [steps.length])

  // ── Side-effecting advances ──────────────────────────────────────────────

  /**
   * "Is this person's company already on DiaEx?" — asked ONCE, inside the
   * account-creation await, so the user never watches a spinner for it.
   *
   * EVERY non-answer resolves to nothing: 404 (the org router is dark while
   * ORGS_LIVE is off), 429, a timeout, an offline device, a malformed body.
   * `orgApi.discover` bounds it at 2.5s and swallows the lot, which is what
   * lets this whole branch ship inert today.
   */
  async function discoverTeam(): Promise<OrgMatch[]> {
    if (!isOrg) return [] // an individual broker has no team to join
    try {
      const r = await orgApi.discover(state.companyName)
      // They already have a request outstanding (a reload, a second tab): the
      // offer would be a button that can only 409. Say nothing, show nothing.
      if (r.pendingRequest?.status === 'pending') return []
      return r.matches.slice(0, 3)
    } catch {
      // Belt and braces. `orgApi.discover` already swallows, and this runs
      // INSIDE the account-creation try — a throw here would report "something
      // went wrong creating your account" for an account that exists.
      return []
    }
  }

  async function createAccount() {
    if (!state.agreed) { fail('Please agree to the Terms & Conditions to continue.'); return }
    if (!/.+@.+\..+/.test(state.email)) { fail('That email address looks off.'); return }
    if (password.length < 8) { fail('Password must be at least 8 characters.'); return }
    setBusy(true)
    setError(null)
    setErrorAction(null)
    try {
      if (!state.accountCreated) {
        await signUp(state.email.trim().toLowerCase(), password)
        // RECORD IT THE INSTANT IT IS TRUE, not once the whole step succeeds.
        //
        // `accountCreated` used to be set only after the profile save landed,
        // which made every failure AFTER `signUp` a one-way door: the Auth user
        // exists, the flag says it doesn't, so pressing Continue again re-runs
        // `signUp` and gets `auth/email-already-in-use` — an error whose only
        // offer is "Sign in instead", from a wizard the user is already signed
        // in to. The account was real and unreachable.
        //
        // The flag now means exactly what it says. A profile-save failure —
        // a 400 from an older API, a dropped connection, a 500 — leaves a
        // RETRYABLE step: same button, same handler, `signUp` skipped, only
        // the save repeated. (The save is a merge server-side, so repeating it
        // is free.)
        patch({ accountCreated: true })
      }
      // Partial save right away — accountType, name and the T&C stamp exist
      // even if the tab dies before the finish step.
      await meApi.signupProfile({
        accountType: state.accountType!,
        contactName: state.contactName.trim(),
        termsAccepted: true,
        tradeRole: tradeRoleFor(state.accountType, state.caps),
        ...(isOrg && state.companyName.trim() ? { companyName: state.companyName.trim() } : {}),
      })
      const matches = await discoverTeam()
      patch({
        accountCreated: true,
        ...(matches.length ? { teamOffer: 'shown' as const, teamOrgs: matches } : {}),
      })
      go(1)
    } catch (err) {
      const code = (err as { code?: string })?.code
      if (code === 'auth/email-already-in-use') {
        // THE SOFT DEAD END, FIXED. This copy has always said "sign in
        // instead?" and nothing was clickable. sessionStorage is deliberately
        // NOT cleared: coming back to /signup resumes where they left off.
        fail('An account with that email already exists.', {
          label: 'Sign in instead',
          testId: 'signup-error-signin',
          run: () => (onSignIn ? onSignIn() : navigate('/signin')),
        })
      } else if (code === 'auth/invalid-email') fail('That email address looks off.')
      else if (code === 'auth/weak-password') fail('That password is too weak. Try a longer one.')
      else if (err instanceof ApiError) fail(err.message)
      else fail('Something went wrong creating your account. Try again.')
    } finally {
      setBusy(false)
    }
  }

  async function finish() {
    setBusy(true)
    setError(null)
    setErrorAction(null)
    try {
      const hear = state.hearAbout === 'Other'
        ? (state.hearAboutOther.trim() ? `Other: ${state.hearAboutOther.trim()}` : 'Other')
        : state.hearAbout
      const validRefs = state.refs
        .map((r) => ({
          name: r.name.trim(), company: r.company.trim() || undefined,
          relationship: r.relationship.trim() || undefined,
          email: r.email.trim() || undefined, phone: r.phone.trim() || undefined,
        }))
        .filter((r) => r.name && (r.email || r.phone))
      // Extra company lines (multi-phone at signup, 2026-10-09). Composed the
      // same way as the company-phone field: a '+'-prefixed entry is taken
      // as-is, otherwise the work-mobile dial code applies. Blank / too-short
      // rows are dropped; the server folds these into the structured `contacts`
      // block, with the verified work mobile staying the one WhatsApp designate.
      const additionalPhones = state.additionalPhones
        .map((p) => {
          const digits = p.number.replace(/\D/g, '')
          if (digits.length < 3) return null
          const number = p.number.trim().startsWith('+') ? `+${digits}` : `${state.dialCode}${digits}`
          const label = p.label.trim()
          return label ? { number, label } : { number }
        })
        .filter((p): p is { number: string; label?: string } => p !== null)

      type SignupBody = SignupProfileBody & { additionalPhones?: Array<{ number: string; label?: string }> }
      const body: SignupBody = {
        accountType: state.accountType!,
        contactName: state.contactName.trim(),
        termsAccepted: true,
        tradeRole: tradeRoleFor(state.accountType, state.caps),
        ...(isOrg && state.companyName.trim() ? { companyName: state.companyName.trim() } : {}),
        ...(state.phone.trim() ? { phone: `${state.dialCode}${state.phone.replace(/\D/g, '')}` } : {}),
        ...(state.companyPhone.trim()
          ? { companyPhone: state.companyPhone.trim().startsWith('+')
              ? `+${state.companyPhone.replace(/\D/g, '')}`
              : `${state.dialCode}${state.companyPhone.replace(/\D/g, '')}` }
          : {}),
        messagingOptIn: state.messagingOptIn,
        ...(state.website.trim() ? { website: state.website.trim() } : {}),
        ...(state.address ? { address: state.address } : {}),
        ...(hear ? { hearAbout: hear } : {}),
        ...(state.accountType === 'broker' && !state.useAffiliation && validRefs.length ? { brokerReferences: validRefs } : {}),
        ...(state.accountType === 'broker' && state.useAffiliation && state.affiliation.trim() ? { brokerAffiliation: state.affiliation.trim() } : {}),
        ...(additionalPhones.length ? { additionalPhones } : {}),
      }
      try {
        await meApi.signupProfile(body)
      } catch (err) {
        // DEPLOY-ORDER SAFETY (see endpoints.ts::signupProfile header). Hosting
        // can ship ahead of functions, so `additionalPhones` — an ADDITIVE key —
        // can meet an older strict-zod API that 400s it as `unrecognized_keys`.
        // It is not in DROPPABLE_SIGNUP_FIELDS, so the api helper rethrows rather
        // than mis-drop a contradiction. Drop exactly that key here and retry
        // once: the account never half-creates, and the extra numbers land once
        // functions catches up (the flat phone/companyPhone still persist).
        if (body.additionalPhones && unrecognizedKeysFrom(err).includes('additionalPhones')) {
          const retry: SignupBody = { ...body }
          delete retry.additionalPhones
          await meApi.signupProfile(retry)
        } else {
          throw err
        }
      }
      if (isOrg && state.docs.length) {
        await kycApi.submit(state.docs)
      }
      // Key removal waits for the done screen's CTA — clearing here would
      // let the shells' auth gates bounce the user before they read it.
      patch({ finished: true })
      go(1)
    } catch (err) {
      fail(err instanceof ApiError ? err.message : 'Could not save your details. Try again.')
    } finally {
      setBusy(false)
    }
  }

  /** Supplier last step — record how they'll send stock, then finish.
   *  A skip ("do it later") must never block the signup: the save is
   *  best-effort there, and the same setup lives on My Stock. */
  async function saveDataSourceAndFinish(skipped: boolean) {
    setBusy(true)
    setError(null)
    setErrorAction(null)
    try {
      await supplierDataApi.save(skipped ? { method: 'later' } : draftToSaveBody(stockDraft))
      if (!skipped) {
        // Answers + media pattern + the human tick ride on their own record.
        if (Object.keys(stockDraft.answers).length || stockDraft.mediaPattern.trim()) {
          await supplierDataApi.saveMapping({
            answers: stockDraft.answers,
            ...(stockDraft.notes.trim() ? { notes: stockDraft.notes.trim() } : {}),
            ...(stockDraft.mediaPattern.trim() ? { mediaPattern: stockDraft.mediaPattern.trim() } : {}),
            confirmed: stockDraft.mappingConfirmed,
          })
        }
        // Finishing the step IS the hand-off — otherwise a supplier who filled
        // everything in sits in `draft` forever and nobody is looking at it.
        await supplierDataApi.submit()
      }
    } catch (err) {
      if (!skipped) {
        fail(err instanceof ApiError ? err.message : 'Could not save your stock setup — try again, or choose “I’ll do this later”.')
        setBusy(false)
        return
      }
    } finally {
      setBusy(false)
    }
    await finish()
  }

  // Per-step validate → advance (the plain steps).
  function next() {
    switch (step) {
      case 'type':
        if (!state.accountType) { fail('Pick the one that fits — you can talk to us later if it changes.'); return }
        go(1); break
      case 'name':
        if (!state.contactName.trim()) { fail('Your name helps suppliers and our team address you properly.'); return }
        go(1); break
      case 'company':
        if (!state.companyName.trim()) { fail('The company name is what we verify — it goes on your account.'); return }
        go(1); break
      case 'caps':
        // A multi-select cannot be wrong — `buy` refuses to be the last one
        // switched off — so there is nothing to validate here.
        go(1); break
      case 'credentials':
        void createAccount(); break
      case 'team':
        // Continue is always live on the offer, whatever they did with it.
        go(1); break
      case 'phone':
        // Reached only from the ▼ nudge — the step owns its buttons (the
        // global Continue is hidden there, see ONE PRIMARY PER VIEW). Moving
        // forward without verifying IS "Verify later": the number is kept,
        // just unverified, and the finish payload sends it either way.
        go(1); break
      case 'website':
        go(1); break
      case 'address':
        if (!state.address) { fail('Complete the address — country included; it sets duties and shipping.'); return }
        go(1); break
      case 'hearAbout':
        if (!state.hearAbout) { fail('Pick one — it genuinely helps us.'); return }
        go(1); break
      case 'docs': {
        // ── THE DEAD END, REMOVED ────────────────────────────────────────
        // This used to be:
        //     if (!state.docs.length) { fail('Upload at least your business
        //       registration or license.'); return }
        // …on the LAST step of a nine-step wizard, AFTER the Firebase account
        // already exists. A supplier at 11pm on a phone, whose GST
        // certificate is paper in a filing cabinet, had three options:
        // abandon, upload a wrong file, or go and find a scanner.
        //
        // It was purely client-side. `finish()` already guards its own
        // `kycApi.submit` call on `state.docs.length`, so a zero-document
        // finish is ALREADY a supported server state: `kycStatus` stays
        // `not_submitted`, the account works, prices stay locked, and /kyc is
        // the standing resubmit surface. The cost is now stated on the step
        // AND on the done screen, which is where honesty belongs — not in a
        // refusal.
        if (state.caps.sell) { go(1); break }   // sellers still have stock-connect
        void finish(); break
      }
      case 'stock': {
        if (!stockDraft.method) {
          fail('Pick how you’ll send us stock — or choose “I’ll do this later”.')
          return
        }
        const check = draftIsSubmittable(stockDraft)
        if (!check.ok) { fail(check.reason!); return }
        void saveDataSourceAndFinish(false); break
      }
      case 'refs': {
        const ok = state.useAffiliation
          ? state.affiliation.trim().length >= 10
          : state.refs.some((r) => r.name.trim() && (r.email.trim() || r.phone.trim()))
        if (!ok) {
          fail(state.useAffiliation
            ? 'Tell us the business and a way to reach them.'
            : 'Add at least one reference with an email or phone.')
          return
        }
        void finish(); break
      }
      default: break
    }
  }

  const canGoBack = stepIndex > 0 && step !== 'done' && !(state.accountCreated && steps[stepIndex - 1] === 'credentials')

  // ── Render ───────────────────────────────────────────────────────────────

  const variants = {
    enter: (dir: number) => reduce ? { opacity: 0 } : { opacity: 0, y: dir > 0 ? 28 : -28 },
    center: { opacity: 1, y: 0 },
    exit: (dir: number) => reduce ? { opacity: 0 } : { opacity: 0, y: dir > 0 ? -24 : 24 },
  }

  return (
    <div className="min-h-dvh flex flex-col relative z-[1] bg-vault-bg" data-testid="signup-wizard">
      {/* Vault atmosphere — the wizard floats as a paper sheet in the same
          midnight world as the landing and sign-in (design-consistency
          bridge). Step content stays LIGHT: forms read best on paper. */}
      <div className="fixed inset-0 pointer-events-none" aria-hidden>
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(110% 85% at 50% 30%, rgba(10,15,30,0) 0%, rgba(5,7,12,0.5) 60%, #05070C 100%)' }}
        />
        <div className="vault-shaft absolute inset-0" />
      </div>

      {/* Gold progress hairline — the top edge is the wizard's odometer. */}
      <div className="fixed top-0 left-0 right-0 h-[3px] bg-vault-cream/10 z-20" aria-hidden>
        <motion.div
          className="h-full bg-vault-gold"
          initial={false}
          animate={{ width: `${Math.round(progress * 100)}%` }}
          transition={reduce ? { duration: 0 } : { duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>

      {/* Chrome */}
      <header
        className="relative flex items-center justify-between px-6 pt-5"
        style={{ paddingTop: 'max(env(safe-area-inset-top), 20px)' }}
      >
        <Link to="/" className="font-vault text-2xl tracking-tight text-vault-cream">DiaEx</Link>
        {step !== 'done' && (
          onSignIn
            ? <button type="button" onClick={onSignIn} className="text-sm text-vault-text-2 hover:text-vault-cream transition-colors" data-testid="signup-signin-link">Sign in</button>
            : <Link to="/signin" className="text-sm text-vault-text-2 hover:text-vault-cream transition-colors" data-testid="signup-signin-link">Sign in</Link>
        )}
      </header>

      {/* Invisible reCAPTCHA anchor for phone linking — must stay mounted. */}
      <div id="signup-recaptcha" />

      <main className="relative flex-1 flex flex-col justify-center px-6 py-10">
        {/* The paper sheet — light interior on the vault, so every step's
            existing light styling (inputs, Stripe elements) reads perfectly. */}
        {/* The docket is the ONE step with two stacks side by side (Needed →
            Going to review), and a 576px sheet wraps every document title onto
            two lines to fit them. It gets a wider sheet; every other step is
            one question and stays narrow. */}
        <div className={`w-full mx-auto bg-cream rounded-lg shadow-lift p-6 sm:p-10 ${step === 'docs' ? 'max-w-3xl' : 'max-w-xl'}`}>
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            <motion.div
              key={step}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={reduce ? { duration: 0.15 } : { duration: 0.34, ease: [0.16, 1, 0.3, 1] }}
            >
              {step !== 'done' && (
                <div className="mb-3 flex items-center gap-3">
                  {/* THE IDENTITY SLOT. The glyph you chose at the fork flies
                      in here and stays for the rest of the wizard — one
                      object, continuous, rather than a choice that vanishes
                      the instant you make it. Not rendered on the fork itself,
                      where the glyph lives on the card it flies FROM. */}
                  {step !== 'type' && state.accountType && (
                    <motion.span
                      layoutId="signup-identity"
                      className="grid h-7 w-7 shrink-0 place-items-center text-ink"
                      transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 340, damping: 34 }}
                      data-testid="signup-identity"
                    >
                      <DoorGlyph type={state.accountType} size={28} />
                    </motion.span>
                  )}
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-navy-300 tabular">
                    {Math.min(stepIndex + 1, steps.length - 1)} / {steps.length - 1}
                  </p>
                  {/* The step changes without a page change, so AT has to be
                      told. The visible fraction alone is silent to a screen
                      reader that has already read this region. */}
                  <p className="sr-only" aria-live="polite">
                    Step {Math.min(stepIndex + 1, steps.length - 1)} of {steps.length - 1}: {STEP_TITLES[step]}
                  </p>
                </div>
              )}

              <StepErrorBoundary
                key={step}
                onRecover={() => { try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* fine */ } onDone() }}
              >
              {step === 'type' && (
                <StepDoors
                  state={state}
                  reduce={reduce}
                  onPick={(door) => {
                    patch({ accountType: door === 'broker' ? 'broker' : typeForCaps(state.caps) })
                    // Selecting IS answering — a beat to see the selection,
                    // then advance directly (next() would re-validate against
                    // this render's pre-selection state — stale closure).
                    window.setTimeout(() => go(1), reduce ? 0 : 240)
                  }}
                />
              )}
              {step === 'caps' && (
                <StepCaps
                  caps={state.caps}
                  onChange={(caps) => patch({ caps, accountType: typeForCaps(caps) })}
                />
              )}
              {step === 'team' && (
                <StepTeam
                  matches={state.teamOrgs}
                  companyName={state.companyName}
                  offer={state.teamOffer}
                  requestedOrgId={state.teamRequestOrgId}
                  onRequested={(orgId) => patch({ teamOffer: 'requested', teamRequestOrgId: orgId })}
                  onCancelled={() => patch({ teamOffer: 'shown', teamRequestOrgId: null })}
                  onDismissed={() => { patch({ teamOffer: 'dismissed', teamRequestOrgId: null }); go(1) }}
                  onReopen={() => patch({ teamOffer: 'shown' })}
                />
              )}
              {step === 'name' && (
                <StepText
                  question="What's your name?"
                  helper="First and last — as your trade partners know you."
                  value={state.contactName}
                  onChange={(v) => patch({ contactName: v })}
                  placeholder="Type your full name"
                  autoComplete="name"
                  next={next}
                  testId="signup-name"
                />
              )}
              {step === 'company' && (
                <StepText
                  question={state.accountType === 'supplier' ? 'What is your company called?' : 'What is your business called?'}
                  helper="The legal or trading name — this is the name we verify."
                  value={state.companyName}
                  onChange={(v) => patch({ companyName: v })}
                  placeholder="Company name"
                  autoComplete="organization"
                  next={next}
                  testId="signup-company"
                />
              )}
              {step === 'credentials' && (
                <StepCredentials
                  state={state}
                  patch={patch}
                  password={password}
                  setPassword={setPassword}
                  busy={busy}
                  next={next}
                />
              )}
              {step === 'phone' && (
                <StepPhone state={state} patch={patch} advance={() => go(1)} setTopError={fail} />
              )}
              {step === 'website' && (
                <StepText
                  question="Where can we find your company online?"
                  helper="Your website — or a public listing that shows your business."
                  value={state.website}
                  onChange={(v) => patch({ website: v })}
                  placeholder="https://…"
                  autoComplete="url"
                  inputMode="url"
                  next={next}
                  skipLabel="We don't have a website"
                  onSkip={() => { patch({ website: '' }); go(1) }}
                  testId="signup-website"
                />
              )}
              {step === 'address' && (
                <AddressStep
                  value={state.address}
                  onChange={(a) => patch({ address: a })}
                  companyName={isOrg ? state.companyName : undefined}
                />
              )}
              {step === 'hearAbout' && (
                <StepHearAbout state={state} patch={patch} next={next} advance={() => go(1)} />
              )}
              {step === 'docs' && (
                <StepDocs
                  docs={state.docs}
                  // The docket is jurisdiction-correct: an Indian company is
                  // asked for GST + PAN, not for a "seller's permit".
                  country={state.address?.country}
                  busy={busy}
                  reduce={reduce}
                  onDocs={patchDocs}
                  setTopError={fail}
                  // Only when a join request is actually pending. Waiting on
                  // an admin who may verify you for free, while being told to
                  // go and scan your incorporation certificate, is the shape
                  // of a wall. They browse with locked prices either way.
                  joinPendingOrgName={state.teamOffer === 'requested'
                    ? (state.teamOrgs.find((o) => o.orgId === state.teamRequestOrgId)?.name
                      ?? state.teamOrgs[0]?.name ?? 'your team')
                    : null}
                  onLater={next}
                  onEnter={next}
                />
              )}
              {step === 'refs' && <StepRefs state={state} patch={patch} />}
              {step === 'stock' && (
                <StepStock
                  draft={stockDraft}
                  onChange={setStockDraft}
                  busy={busy}
                  onSkip={() => void saveDataSourceAndFinish(true)}
                />
              )}
              {step === 'done' && (
                <StepDone
                  name={state.contactName}
                  onDone={() => {
                    try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* fine */ }
                    onDone()
                  }}
                  // The other half of removing the docs block: someone who
                  // finished with nothing filed must leave here KNOWING what
                  // it costs and holding the link that fixes it. /kyc exists
                  // on both shells and is wired to the same upload.
                  needsDocs={isOrg && !state.docs.length}
                  onAddDocs={() => {
                    try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* fine */ }
                    navigate('/kyc')
                  }}
                  reduce={reduce}
                />
              )}
              </StepErrorBoundary>

              {error && (
                <div className="mt-4" role="alert" data-testid="signup-error">
                  <p className="text-sm text-danger">{error}</p>
                  {/* An error that names a way out ships the CONTROL that
                      takes it. "…sign in instead?" as unclickable prose was a
                      shipped soft dead end. */}
                  {errorAction && (
                    <PressScale
                      feel="tap"
                      onClick={errorAction.run}
                      className="mt-2 inline-flex items-center min-h-[44px] text-sm font-semibold text-ink underline underline-offset-4 hover:text-gold-600"
                      data-testid={errorAction.testId}
                    >
                      {errorAction.label}
                    </PressScale>
                  )}
                </div>
              )}

              {/* ONE PRIMARY PER VIEW. The unanswered join offer owns its own
                  two controls — "Request to join" IS the primary there — so
                  the wizard's standing Continue would be a second gold button
                  competing with it, and a third way to answer a question that
                  has exactly two answers. Both of the step's controls advance,
                  and the ▼ nudge below still moves forward, so nothing is
                  trapped.

                  The PHONE step is the other owner of its own actions: "Text
                  me the code" is the primary and "Verify later" the way past
                  it (and, in code mode, "Verify" / resend / "Verify later").
                  The standing Continue rendered NEXT TO "Text me the code" as
                  a second gold button that did nothing (next() had no 'phone'
                  case) — two competing CTAs, one of them dead. Hidden here;
                  the ▼ nudge still advances via next()'s 'phone' case. */}
              {step !== 'type' && step !== 'done' && step !== 'phone' && !(step === 'team' && state.teamOffer === 'shown') && (
                <div className="mt-8 flex items-center gap-4">
                  <button
                    type="button"
                    onClick={next}
                    disabled={busy}
                    className="btn-primary h-[52px] px-8 text-base"
                    data-testid="signup-next"
                  >
                    {busy ? (step === 'credentials' ? 'Creating your account…' : 'Saving…')
                      : step === 'refs' || step === 'stock' || (step === 'docs' && !state.caps.sell) ? 'Finish'
                      : step === 'credentials' ? 'Create account'
                      : 'Continue'}
                  </button>
                  <span className="hidden md:inline text-xs text-navy-300">
                    press <kbd className="font-semibold text-navy-500">Enter ↵</kbd>
                  </span>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Back / forward nudge — bottom right, typeform-style */}
      {step !== 'done' && (
        <div
          className="fixed bottom-5 right-5 flex gap-px rounded-md overflow-hidden shadow-elev-1 z-10"
          style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
        >
          <button
            type="button"
            aria-label="Previous question"
            onClick={() => canGoBack && go(-1)}
            disabled={!canGoBack}
            className="h-11 w-11 grid place-items-center bg-paper text-navy-500 disabled:opacity-30 hover:text-gold-600"
            data-testid="signup-back"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
          </button>
          <button
            type="button"
            aria-label="Next question"
            onClick={next}
            className="h-11 w-11 grid place-items-center bg-paper text-navy-500 hover:text-gold-600"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Step: the fork — two doors (the signature screen) ──────────────────────

/**
 * TWO DOORS, not three cards.
 *
 * The old fork asked "supplier / buyer / broker" and made you pick one, which
 * quietly forced a company that does both to lie about itself — and there was
 * no way to say so afterwards. The real fork is ORGANIZATION vs INDIVIDUAL,
 * because that is what decides what we verify (company papers vs references)
 * and what a seat means. Buy/sell is a capability, asked next, where both
 * answers can be true.
 *
 * Deliberately `.stone-card` (solid surface, painted rim, hover lift) and NOT
 * `.lqd-glass`: this codebase's own rule is that `backdrop-filter` belongs to
 * chrome, never to items in a list, and two stacked glass panels over the
 * vault gradient is exactly the jank the CSS comments warn about on a low-end
 * Android. The "glass reacts to you" move lives BEHIND the sheet instead —
 * see `.door-light`.
 */
function StepDoors({ state, reduce, onPick }: {
  state: WizardState
  reduce: boolean
  onPick: (door: DoorId) => void
}) {
  const [lit, setLit] = useState<DoorId | null>(null)
  const chosenDoor: DoorId | null =
    state.accountType === 'broker' ? 'broker' : isOrgType(state.accountType) ? 'org' : null
  return (
    <div className="relative">
      {/* The door light. Two mirrored ambient fields, painted behind the paper
          sheet, opacity-only. Hover OR FOCUS a door and its half warms — the
          focus half is what keeps this from being a dead material for anyone
          on a keyboard. */}
      <div className="door-light -inset-x-10 -inset-y-16 absolute" aria-hidden>
        <i className="lqd-ambient" style={{ '--lit': lit === 'org' ? 0.55 : lit ? 0.12 : 0.22 } as React.CSSProperties} />
        <i className="lqd-ambient" style={{ '--lit': lit === 'broker' ? 0.55 : lit ? 0.12 : 0.22 } as React.CSSProperties} />
      </div>

      <div className="relative">
        <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
          Who is signing up?
        </h1>
        <p className="mt-2 text-sm text-navy-500">
          This decides what we verify — company papers, or your trade references.
        </p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {DOOR_CARDS.map(({ door, title, blurb, Icon }) => {
            const active = chosenDoor === door
            return (
              <PressScale
                key={door}
                feel="select"
                lift
                onClick={() => onPick(door)}
                onPointerEnter={() => setLit(door)}
                onPointerLeave={() => setLit(null)}
                onFocus={() => setLit(door)}
                onBlur={() => setLit(null)}
                className={`group relative text-left rounded-md border p-5 min-h-[7.5rem] stone-card transition-colors duration-default ease-premium ${
                  active ? 'border-gold-500 bg-gold-050' : 'border-line bg-paper'
                }`}
                data-testid={`signup-door-${door}`}
                aria-pressed={active}
                aria-describedby={`door-${door}-blurb`}
              >
                <motion.span
                  // The chosen glyph is the SAME OBJECT as the one in the sheet
                  // header from the next step on. Going back plays it in
                  // reverse for free.
                  {...(active ? { layoutId: 'signup-identity' } : {})}
                  transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 340, damping: 34 }}
                  className={`block ${active ? 'text-ink' : 'text-navy-500 group-hover:text-ink'} transition-colors`}
                >
                  <Icon active={active} />
                </motion.span>
                <span className="mt-4 block font-semibold text-ink">{title}</span>
                <span id={`door-${door}-blurb`} className="mt-1 block text-xs leading-relaxed text-navy-500">{blurb}</span>
                <span
                  aria-hidden
                  className={`absolute top-4 right-4 h-4 w-4 rotate-45 rounded-[3px] border transition-all ${
                    active ? 'bg-gold-500 border-gold-500' : 'border-line-strong bg-transparent'
                  }`}
                />
              </PressScale>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Step: capabilities (organizations only) ────────────────────────────────

/**
 * "What will you do here?" — a DISCLOSURE, not a second fork.
 *
 * Two checkboxes, both may be on, `buy` pre-checked and refusing to be the
 * last one switched off. There is deliberately no third option, no
 * auto-advance and no error path: the instant this can be answered "wrong" it
 * has become another decision, and the fork was supposed to be the only one.
 *
 * The sell row states its own cost ("Adds one step") because being ambushed by
 * a stock-connect step eight screens later is how a wizard loses people.
 */
function StepCaps({ caps, onChange }: {
  caps: Capabilities
  onChange: (c: Capabilities) => void
}) {
  function toggle(key: keyof Capabilities, on: boolean) {
    const next = { ...caps, [key]: on }
    // Un-ticking the last box would be an empty answer, and an empty answer
    // needs an error state. A checkbox that refuses to be the last one off is
    // better than a toast that tells you off.
    if (!next.buy && !next.sell) next.buy = true
    feedback.toggle(next[key])
    onChange(next)
  }
  return (
    <div data-testid="signup-caps">
      <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
        What will you do on DiaEx?
      </h1>
      <p className="mt-2 text-sm text-navy-500">Both is normal — most companies buy and sell.</p>
      <div className="mt-8 space-y-3">
        {CAP_ROWS.map(({ key, title, hint, Icon }) => {
          const on = caps[key]
          return (
            <label
              key={key}
              // The ring lives on the LABEL, not the input: the input is
              // `sr-only`, i.e. clipped to 1px, so the global :focus-visible
              // ring would be drawn inside a box nobody can see. A keyboard
              // user would have had no focus indicator at all.
              className="group flex items-center gap-4 rounded-md border border-line bg-paper px-4 py-3 min-h-[60px] cursor-pointer select-none transition-all duration-default ease-premium has-[:checked]:border-gold-500 has-[:checked]:bg-gold-050 has-[:focus-visible]:shadow-[0_0_0_3px_var(--gold-300)]"
              // The tappable thing is the row, so the row carries the id —
              // the same convention as the document tiles below. The input
              // keeps its own for state assertions.
              data-testid={`signup-cap-${key}`}
            >
              <input
                type="checkbox"
                className="sr-only peer"
                checked={on}
                onChange={(e) => toggle(key, e.target.checked)}
                data-testid={`signup-cap-${key}-input`}
              />
              <span className={`shrink-0 ${on ? 'text-ink' : 'text-navy-300'} transition-colors`} aria-hidden>
                <Icon active={on} size={28} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-ink">{title}</span>
                <span className="block text-xs leading-relaxed text-navy-500">{hint}</span>
              </span>
              {/* The same rotated-square tick as the doors — the fork's visual
                  signature, carried through. */}
              <span
                aria-hidden
                className={`shrink-0 h-5 w-5 rotate-45 rounded-[3px] border transition-all ${
                  on ? 'bg-gold-500 border-gold-500' : 'border-line-strong bg-transparent'
                }`}
              />
            </label>
          )
        })}
      </div>
    </div>
  )
}

// ─── Step: "your team is already here" (the join branch) ────────────────────

/**
 * THE 10x MOMENT — and the one screen on this surface with a security
 * invariant behind it.
 *
 * An employee who signs up organically used to create a DUPLICATE org and
 * become its owner: two orgs for one company, split KYC, split seats, and the
 * real company's verification not inherited. This converts that into the best
 * moment in onboarding — the product recognises your company — and it is the
 * first time "you inherit their verification", the entire reason a seat is
 * worth money, is said to the person it benefits.
 *
 * What it is NOT:
 *   • it never auto-joins. A match is an OFFER; an owner/admin approves.
 *   • it never blocks. "This is a different company" is always on screen, the
 *     request is undoable, a failure offers Try again AND Continue without it,
 *     and the wizard proceeds to a fully working solo account either way.
 *   • it never shows anything but a name. The server sends no member count,
 *     no verification state and no owner identity, deliberately.
 */
function StepTeam({
  matches, companyName, offer, requestedOrgId,
  onRequested, onCancelled, onDismissed, onReopen,
}: {
  matches: OrgMatch[]
  companyName: string
  /** Persisted, so a Back or a reload renders what they ANSWERED — never the
   *  same question a second time. */
  offer: TeamOffer
  requestedOrgId: string | null
  onRequested: (orgId: string) => void
  onCancelled: () => void
  onDismissed: () => void
  onReopen: () => void
}) {
  /** Transient only. Everything durable is the wizard's `teamOffer`. */
  const [sendingOrgId, setSendingOrgId] = useState<string | null>(null)
  const [failure, setFailure] = useState<{ org: OrgMatch; message: string } | null>(null)
  const [undoable, setUndoable] = useState(false)

  const requestedOrg = matches.find((m) => m.orgId === requestedOrgId) ?? matches[0] ?? null

  // The undo window. Long enough to catch "wrong company", short enough that
  // the receipt settles rather than nagging. When it closes the ROW stays —
  // only the button goes, and the request is still cancellable from the
  // account screen (GET/DELETE /org/join-requests/mine).
  useEffect(() => {
    if (offer !== 'requested') { setUndoable(false); return }
    setUndoable(true)
    const t = window.setTimeout(() => setUndoable(false), 8000)
    return () => window.clearTimeout(t)
  }, [offer])

  async function request(org: OrgMatch) {
    setFailure(null)
    setSendingOrgId(org.orgId)
    try {
      await orgApi.requestJoin(org.orgId, companyName)
      feedback.success()
      onRequested(org.orgId)
    } catch (err) {
      // A 409 means a request is ALREADY pending — which is the outcome the
      // user wanted. Never show that as a failure.
      if (err instanceof ApiError && err.status === 409) {
        onRequested(org.orgId)
        return
      }
      setFailure({
        org,
        message: err instanceof ApiError ? err.message : 'We couldn’t send that request just now.',
      })
    } finally {
      setSendingOrgId(null)
    }
  }

  async function undo() {
    setUndoable(false)
    onCancelled()
    feedback.dim()
    // Best effort. If it fails the row is still cancellable from the account
    // screen, which is why this can never block or error here.
    try { await orgApi.cancelJoinRequest() } catch { /* noop */ }
  }

  // ── ANSWERED: they asked ───────────────────────────────────────────────
  if (offer === 'requested' && requestedOrg) {
    return (
      <div>
        <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
          We’ve asked.
        </h1>
        <div
          className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-line bg-paper px-4 py-3 min-h-[44px]"
          role="status"
          data-testid="signup-team-pending"
        >
          {/* The company's mark STRIKES in at the moment the ask lands — one
              settle, never a loop. Its reduced-motion block ships in the same
              commit (styles/index.css, "signup fork"). */}
          <OrgCrest orgId={requestedOrg.orgId} size={28} className="crest-strike" />
          <p className="text-sm text-navy-500 flex-1 min-w-[12rem]">
            An admin at <span className="font-semibold text-ink">{requestedOrg.name}</span> has to
            approve you. We’ll email you either way.
          </p>
          {undoable && (
            <PressScale
              feel={false}
              onClick={() => void undo()}
              className="min-h-[44px] px-2 text-sm font-semibold text-navy-500 underline underline-offset-4 hover:text-danger"
              data-testid="signup-team-undo"
            >
              Undo
            </PressScale>
          )}
        </div>
        <p className="mt-4 text-xs text-navy-300">
          Nothing is on hold: finish signing up and keep browsing. If they approve you, you
          inherit their verification.
        </p>
      </div>
    )
  }

  // ── ANSWERED: they said it is a different company ──────────────────────
  // Reachable only by pressing Back, and REVERSIBLE — a dismissal you cannot
  // take back is a dead end wearing a "no thanks" label.
  if (offer === 'dismissed') {
    return (
      <div data-testid="signup-team-dismissed">
        <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
          You’re creating your own organization.
        </h1>
        <p className="mt-2 text-sm text-navy-500">
          That’s the default — you’ll be verified on your own papers, and you can invite your
          team onto your seats afterwards.
        </p>
        <PressScale
          feel="tap"
          onClick={onReopen}
          className="btn-secondary mt-6"
          data-testid="signup-team-reopen"
        >
          Actually, ask to join {requestedOrg?.name ?? 'them'}
        </PressScale>
      </div>
    )
  }

  // ── THE OFFER ──────────────────────────────────────────────────────────
  const byDomain = matches.some((m) => m.basis === 'email_domain')
  return (
    <div>
      <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
        {matches.length > 1
          ? 'Is one of these your company?'
          : `Do you work at ${matches[0]?.name ?? 'this company'}?`}
      </h1>
      <p className="mt-2 text-sm text-navy-500">
        {byDomain
          // The domain match is the strong one, and saying WHY we think so is
          // what makes this read as competence rather than surveillance.
          ? 'Your work email says you might. Ask to join and you inherit their verification — prices and supplier contacts unlock the moment an admin approves, with no separate review.'
          : 'A company with that name is already on DiaEx. If it’s yours, ask to join and you inherit their verification instead of starting a second account for the same company.'}
      </p>

      {failure && (
        <div
          className="mt-6 rounded-md border border-danger/40 bg-danger/5 px-4 py-3"
          role="alert"
          data-testid="signup-team-failed"
        >
          <p className="text-sm text-danger">{failure.message}</p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <PressScale
              feel="tap"
              onClick={() => void request(failure.org)}
              className="btn-secondary"
              data-testid="signup-team-retry"
            >
              Try again
            </PressScale>
            <PressScale
              feel="dim"
              onClick={onDismissed}
              className="min-h-[44px] text-sm text-navy-500 underline underline-offset-4 hover:text-gold-600"
              data-testid="signup-team-continue-without"
            >
              Continue without it
            </PressScale>
          </div>
        </div>
      )}

      <div className="mt-8 space-y-3">
        {matches.map((org, i) => (
          <section
            key={org.orgId}
            aria-labelledby={`team-${org.orgId}`}
            className="stone-card detail-rise flex flex-wrap items-center gap-4 rounded-md border border-line bg-paper p-5"
            style={{ '--d': `${0.06 + i * 0.05}s` } as React.CSSProperties}
            data-testid="signup-team-found"
          >
            <OrgCrest orgId={org.orgId} size={48} name={org.name} className="shrink-0" />
            <div className="min-w-0 flex-1">
              <p id={`team-${org.orgId}`} className="font-semibold text-ink break-words">{org.name}</p>
              <p className="mt-0.5 text-xs text-navy-500">
                {org.basis === 'email_domain' ? 'Matched your work email domain' : 'Matched the company name you typed'}
              </p>
            </div>
            <PressScale
              feel="select"
              disabled={sendingOrgId !== null}
              onClick={() => void request(org)}
              className="btn-primary h-[52px] px-6 text-base"
              data-testid="signup-team-join"
            >
              {sendingOrgId === org.orgId ? 'Asking…' : 'Request to join'}
            </PressScale>
          </section>
        ))}
      </div>

      <div className="mt-6">
        <PressScale
          feel="dim"
          onClick={onDismissed}
          className="min-h-[44px] text-sm text-navy-500 underline underline-offset-4 hover:text-gold-600"
          data-testid="signup-team-separate"
        >
          {matches.length > 1 ? 'None of these — create a separate organization' : 'This is a different company'}
        </PressScale>
        <p className="mt-2 text-xs text-navy-300">
          You keep your own account either way — asking never puts your signup on hold.
        </p>
      </div>
    </div>
  )
}

// ─── Step: single text question ─────────────────────────────────────────────

function StepText(props: {
  question: string
  helper?: string
  value: string
  onChange: (v: string) => void
  placeholder: string
  autoComplete?: string
  inputMode?: 'url' | 'text'
  multiline?: boolean
  next: () => void
  skipLabel?: string
  onSkip?: () => void
  testId: string
}) {
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  useEffect(() => {
    const t = window.setTimeout(() => ref.current?.focus(), 380)
    return () => window.clearTimeout(t)
  }, [])
  return (
    <form onSubmit={(e) => { e.preventDefault(); props.next() }}>
      <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">{props.question}</h1>
      {props.helper && <p className="mt-2 text-sm text-navy-500">{props.helper}</p>}
      {props.multiline ? (
        <textarea
          ref={(el) => { ref.current = el }}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); props.next() }
          }}
          placeholder={props.placeholder}
          rows={2}
          autoComplete={props.autoComplete}
          className="wizard-input mt-8 resize-none"
          data-testid={props.testId}
        />
      ) : (
        <input
          ref={(el) => { ref.current = el }}
          type="text"
          inputMode={props.inputMode}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          placeholder={props.placeholder}
          autoComplete={props.autoComplete}
          className="wizard-input mt-8"
          data-testid={props.testId}
        />
      )}
      {props.skipLabel && (
        <button type="button" onClick={props.onSkip} className="mt-3 text-sm text-navy-300 hover:text-gold-600 underline underline-offset-4">
          {props.skipLabel}
        </button>
      )}
      {/* Hidden submit so Enter works in the single input form */}
      <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
    </form>
  )
}

// ─── Step: credentials + T&C (creates the account) ──────────────────────────

function StepCredentials({ state, patch, password, setPassword, busy, next }: {
  state: WizardState
  patch: (p: Partial<WizardState>) => void
  password: string
  setPassword: (v: string) => void
  busy: boolean
  next: () => void
}) {
  const emailRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const t = window.setTimeout(() => emailRef.current?.focus(), 380)
    return () => window.clearTimeout(t)
  }, [])
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!busy) next() }}>
      <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
        Create your login
      </h1>
      <p className="mt-2 text-sm text-navy-500">Work email is best — it's where your verification result lands.</p>
      <div className="mt-8 space-y-3">
        <input
          ref={emailRef}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Work email"
          value={state.email}
          onChange={(e) => patch({ email: e.target.value })}
          className="input h-[52px] text-base"
          required
          disabled={state.accountCreated}
          data-testid="signup-email"
        />
        {!state.accountCreated && (
          <PasswordInput
            value={password}
            onChange={setPassword}
            placeholder="Password (min 8 characters)"
            autoComplete="new-password"
            required
            minLength={8}
            testId="signup-password"
          />
        )}
        <label className="flex items-start gap-3 pt-2 cursor-pointer select-none min-h-[44px]">
          <input
            type="checkbox"
            checked={state.agreed}
            onChange={(e) => patch({ agreed: e.target.checked })}
            className="mt-0.5 h-5 w-5 accent-[#c9a94a]"
            data-testid="signup-terms"
          />
          <span className="text-sm text-navy-500 leading-relaxed">
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="text-ink underline underline-offset-2 hover:text-gold-600">Terms &amp; Conditions</Link>
            {' '}and{' '}
            <Link to="/privacy" target="_blank" className="text-ink underline underline-offset-2 hover:text-gold-600">Privacy Policy</Link>.
          </span>
        </label>
      </div>
      <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
    </form>
  )
}

// ─── Step: phone + OTP ──────────────────────────────────────────────────────

function StepPhone({ state, patch, advance, setTopError }: {
  state: WizardState
  patch: (p: Partial<WizardState>) => void
  advance: () => void
  setTopError: (e: string | null) => void
}) {
  const [mode, setMode] = useState<'enter' | 'code'>('enter')
  const [code, setCode] = useState('')
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null)
  const [sending, setSending] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [cooldown, setCooldown] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const t = window.setTimeout(() => inputRef.current?.focus(), 380)
    return () => window.clearTimeout(t)
  }, [mode])

  useEffect(() => {
    if (cooldown <= 0) return
    const t = window.setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => window.clearTimeout(t)
  }, [cooldown])

  const e164 = `${state.dialCode}${state.phone.replace(/\D/g, '')}`

  // Extra company lines — the work mobile above stays the business WhatsApp
  // designate; these are additional numbers buyers can reach the company on.
  const addExtraPhone = () =>
    patch({ additionalPhones: [...state.additionalPhones, { number: '', label: '' }] })
  const updateExtraPhone = (i: number, p: Partial<ExtraPhone>) =>
    patch({ additionalPhones: state.additionalPhones.map((row, idx) => (idx === i ? { ...row, ...p } : row)) })
  const removeExtraPhone = (i: number) =>
    patch({ additionalPhones: state.additionalPhones.filter((_, idx) => idx !== i) })

  async function sendCode() {
    if (state.phone.replace(/\D/g, '').length < 5) {
      setLocalError('That number looks too short.')
      return
    }
    setSending(true)
    setLocalError(null)
    try {
      const conf = await startPhoneLink(e164, 'signup-recaptcha')
      setConfirmation(conf)
      setMode('code')
      setCooldown(30)
    } catch (err) {
      const codeStr = (err as { code?: string })?.code || ''
      if (codeStr === 'auth/invalid-phone-number') {
        setLocalError('That number doesn’t look valid — check the country code.')
      } else if (codeStr === 'auth/too-many-requests') {
        setLocalError('Too many attempts — you can verify later from your profile.')
      } else {
        // SMS not configured / blocked region / reCAPTCHA hiccup: never block
        // the signup on this. The number is kept, just unverified.
        setLocalError('We couldn’t send a code right now — continue and verify later.')
      }
    } finally {
      setSending(false)
    }
  }

  async function verify() {
    if (!confirmation || code.trim().length < 6) return
    setVerifying(true)
    setLocalError(null)
    try {
      await confirmPhoneLink(confirmation, code.trim())
      patch({ phoneVerified: true })
      setTopError(null)
      advance()
    } catch {
      setLocalError('That code didn’t match. Check the SMS and try again.')
    } finally {
      setVerifying(false)
    }
  }

  return (
    <div>
      <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
        {mode === 'enter' ? 'Your work phone' : 'Enter the code we texted'}
      </h1>
      <p className="mt-2 text-sm text-navy-500">
        {mode === 'enter'
          ? 'We’ll text a 6-digit code to verify it — suppliers reach you here.'
          : `Sent to ${e164}. It can take a few seconds to arrive.`}
      </p>

      {mode === 'enter' ? (
        <form onSubmit={(e) => { e.preventDefault(); void sendCode() }}>
          {/* Two numbers, two visible labels — placeholder-only fields turn
              into anonymous boxes the moment they're filled (the DataSourceSetup
              label idiom, which is the wizard's for multi-field screens). */}
          <label className="mt-8 block text-[13px] text-navy-500">
            <span className="mb-1 block font-medium text-ink">Work mobile — we&rsquo;ll text a code to verify</span>
            <span className="mb-2 block text-[12px] text-navy-400">
              This becomes your business WhatsApp number — the one buyers message. You can change it any time in your profile.
            </span>
            <div className="flex gap-3">
              <select
                value={state.dialCode}
                onChange={(e) => patch({ dialCode: e.target.value })}
                className="input h-[52px] w-32 text-base px-2"
                aria-label="Country code"
                data-testid="signup-dialcode"
              >
                {DIAL_CODES.map((d) => (
                  <option key={d.code} value={d.code}>{d.code} {d.label}</option>
                ))}
              </select>
              <input
                ref={inputRef}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="Phone number"
                value={state.phone}
                onChange={(e) => patch({ phone: e.target.value })}
                className="wizard-input flex-1"
                data-testid="signup-phone"
              />
            </div>
          </label>
          {/* Company/office line — optional second number (Tej 2026-08-21).
              A "+"-prefixed entry is taken as-is (a different-country office
              is normal); otherwise the dial code above applies. */}
          <label className="mt-4 block text-[13px] text-navy-500">
            <span className="mb-1 block font-medium text-ink">Company phone <span className="font-normal text-navy-500">(optional)</span> — office or landline</span>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="e.g. +91 22 4000 0000"
              value={state.companyPhone}
              onChange={(e) => patch({ companyPhone: e.target.value })}
              className="wizard-input w-full"
              data-testid="signup-company-phone"
            />
          </label>

          {/* Additional company lines (multi-phone at signup, 2026-10-09).
              Optional, repeatable — a warehouse, a second office, a cell. Each
              is a plain number + an optional label; a '+'-prefixed entry is
              taken as-is, otherwise the work-mobile dial code applies (the
              company-phone idiom). The work mobile above stays the single
              business WhatsApp designate. Employees get their OWN login + phone
              via the email invite after signup — these are company lines, not
              teammate seats. */}
          <div className="mt-4">
            <span className="mb-1 block text-[13px] font-medium text-ink">Other numbers <span className="font-normal text-navy-500">(optional)</span></span>
            <span className="mb-2 block text-[12px] text-navy-400">
              Extra company lines buyers can reach you on. To give a teammate their own login, invite them after signup.
            </span>
            {state.additionalPhones.length > 0 && (
              <div className="space-y-2">
                {state.additionalPhones.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="off"
                      placeholder="e.g. +91 22 4000 0000"
                      value={p.number}
                      onChange={(e) => updateExtraPhone(i, { number: e.target.value })}
                      className="wizard-input flex-1 min-w-0"
                      aria-label={`Additional number ${i + 1}`}
                      data-testid="signup-additional-number"
                    />
                    <input
                      type="text"
                      maxLength={40}
                      placeholder="Label"
                      value={p.label}
                      onChange={(e) => updateExtraPhone(i, { label: e.target.value })}
                      className="wizard-input w-24 min-w-0"
                      aria-label={`Label for additional number ${i + 1}`}
                      data-testid="signup-additional-label"
                    />
                    <button
                      type="button"
                      onClick={() => removeExtraPhone(i)}
                      aria-label={`Remove additional number ${i + 1}`}
                      className="shrink-0 px-2 text-xl leading-none text-navy-300 hover:text-danger min-h-[44px] min-w-[44px]"
                      data-testid="signup-additional-remove"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={addExtraPhone}
              className="mt-2 text-[13px] font-semibold text-gold-600 hover:underline min-h-[44px]"
              data-testid="signup-add-number"
            >
              + Add another number
            </button>
          </div>

          {/* SMS/WhatsApp opt-in — Twilio-compliance consent, UNCHECKED by
              default (pre-checked boxes are not consent). The server stamps
              the timestamp; both yes and no are recorded. Same checkbox idiom
              as the T&C consent on the credentials step. */}
          <label className="mt-6 flex items-start gap-3 cursor-pointer select-none min-h-[44px]" data-testid="signup-messaging-optin">
            <input
              type="checkbox"
              checked={state.messagingOptIn}
              onChange={(e) => patch({ messagingOptIn: e.target.checked })}
              className="mt-0.5 h-5 w-5 shrink-0 accent-[#c9a94a]"
              data-testid="signup-messaging-optin-input"
            />
            <span className="text-[13px] leading-relaxed text-navy-500">
              I agree to receive account, order and service messages from DiaEx by{' '}
              <span className="font-semibold text-ink">SMS and WhatsApp</span> at the number(s) above.
              Message frequency varies; message &amp; data rates may apply. Reply{' '}
              <span className="font-semibold text-ink">STOP</span> to opt out or{' '}
              <span className="font-semibold text-ink">HELP</span> for help at any time. See our{' '}
              <a href="/privacy" target="_blank" rel="noopener" className="underline underline-offset-2 text-navy-300 hover:text-gold-600">Privacy Policy</a> and{' '}
              <a href="/terms" target="_blank" rel="noopener" className="underline underline-offset-2 text-navy-300 hover:text-gold-600">Terms</a>.
            </span>
          </label>

          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <button type="submit" disabled={sending} className="btn-primary h-[52px] px-8 text-base" data-testid="signup-send-code">
              {sending ? 'Sending…' : 'Text me the code'}
            </button>
            <button
              type="button"
              onClick={() => { setTopError(null); advance() }}
              className="text-sm text-navy-300 hover:text-gold-600 underline underline-offset-4 min-h-[44px]"
              data-testid="signup-phone-skip"
            >
              Verify later
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void verify() }}>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="••••••"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="wizard-input mt-8 tracking-[0.5em] text-2xl w-56"
            data-testid="signup-otp"
          />
          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <button type="submit" disabled={verifying || code.length < 6} className="btn-primary h-[52px] px-8 text-base" data-testid="signup-verify-code">
              {verifying ? 'Checking…' : 'Verify'}
            </button>
            <button
              type="button"
              disabled={cooldown > 0}
              onClick={() => { setMode('enter'); setCode('') }}
              className="text-sm text-navy-300 hover:text-gold-600 underline underline-offset-4 disabled:opacity-50 min-h-[44px]"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Change number / resend'}
            </button>
            <button
              type="button"
              onClick={() => { setTopError(null); advance() }}
              className="text-sm text-navy-300 hover:text-gold-600 underline underline-offset-4 min-h-[44px]"
            >
              Verify later
            </button>
          </div>
        </form>
      )}
      {localError && <p className="mt-4 text-sm text-danger" role="alert">{localError}</p>}
    </div>
  )
}

// ─── Step: how did you hear about us ────────────────────────────────────────

function StepHearAbout({ state, patch, next, advance }: {
  state: WizardState
  patch: (p: Partial<WizardState>) => void
  next: () => void
  /** Direct advance for tap-selections — next() would re-validate against
   *  this render's stale (pre-selection) state. */
  advance: () => void
}) {
  return (
    <div>
      <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
        How did you hear about DiaEx?
      </h1>
      <p className="mt-2 text-sm text-navy-500">One tap — it genuinely helps us find more of the trade.</p>
      <div className="mt-8 flex flex-wrap gap-2.5">
        {HEAR_OPTIONS.map((opt) => {
          const active = state.hearAbout === opt
          return (
            <button
              key={opt}
              type="button"
              onClick={() => {
                patch({ hearAbout: opt })
                if (opt !== 'Other') window.setTimeout(advance, 220)
              }}
              className={`inline-flex items-center rounded-full border px-4 h-11 text-sm font-medium transition-all duration-default ease-premium ${
                active
                  ? 'border-gold-500 bg-gold-050 text-ink shadow-elev-1'
                  : 'border-line bg-paper text-navy-500 hover:border-navy-300 hover:text-ink'
              }`}
              data-testid={`signup-hear-${opt.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              aria-pressed={active}
            >
              {opt}
            </button>
          )
        })}
      </div>
      {state.hearAbout === 'Other' && (
        <input
          type="text"
          autoFocus
          value={state.hearAboutOther}
          onChange={(e) => patch({ hearAboutOther: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); next() } }}
          placeholder="Tell us where"
          className="wizard-input mt-6"
          data-testid="signup-hear-other"
        />
      )}
    </div>
  )
}

// ─── Step: company documents ────────────────────────────────────────────────
//
// THE DOCKET now lives in ./StepDocs.tsx — a jurisdiction-correct list of
// papers, photographable on the spot, that files itself into a review packet
// and never blocks. Kept out of this file because it is the largest step in
// the wizard and its own unit under test.

// ─── Step: broker references / affiliation ──────────────────────────────────

function StepRefs({ state, patch }: {
  state: WizardState
  patch: (p: Partial<WizardState>) => void
}) {
  const setRef = (i: number, p: Partial<BrokerRef>) => {
    const refs = state.refs.map((r, idx) => (idx === i ? { ...r, ...p } : r))
    patch({ refs })
  }
  return (
    <div>
      <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
        Last step — vouch for your work
      </h1>
      <p className="mt-2 text-sm text-navy-500">
        Independent brokers are verified through people who've traded with them — or a business that can confirm the relationship.
      </p>

      <div className="mt-6 inline-flex rounded-full border border-line bg-paper p-1" role="tablist">
        {([
          { key: false, label: 'Trade references' },
          { key: true, label: 'A business vouches for me' },
        ] as const).map((t) => (
          <button
            key={String(t.key)}
            type="button"
            role="tab"
            aria-selected={state.useAffiliation === t.key}
            onClick={() => patch({ useAffiliation: t.key })}
            className={`rounded-full px-4 h-9 text-sm font-medium transition-colors ${
              state.useAffiliation === t.key ? 'bg-gold-500 text-navy-900' : 'text-navy-500 hover:text-navy-900'
            }`}
            data-testid={t.key ? 'signup-refs-affiliation-tab' : 'signup-refs-tab'}
          >
            {t.label}
          </button>
        ))}
      </div>

      {state.useAffiliation ? (
        <textarea
          value={state.affiliation}
          onChange={(e) => patch({ affiliation: e.target.value })}
          rows={3}
          placeholder="Which business, your relationship to it, and who we can contact — e.g. “Contractor for Shreeji Gems, Mumbai — ask for Vikram, +91 …”"
          className="wizard-input mt-6 resize-none"
          data-testid="signup-affiliation"
        />
      ) : (
        <div className="mt-6 space-y-4">
          {state.refs.map((r, i) => (
            <div key={i} className="card space-y-2.5" data-testid={`signup-ref-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-navy-300">Reference {i + 1}</span>
                {state.refs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => patch({ refs: state.refs.filter((_, idx) => idx !== i) })}
                    className="text-xs text-navy-300 hover:text-danger min-h-[44px] px-2"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="grid gap-2.5 sm:grid-cols-2">
                <input type="text" placeholder="Full name" value={r.name} autoComplete="off"
                  onChange={(e) => setRef(i, { name: e.target.value })} className="input h-11 text-base sm:col-span-2" />
                <input type="text" placeholder="Their company (optional)" value={r.company} autoComplete="off"
                  onChange={(e) => setRef(i, { company: e.target.value })} className="input h-11 text-base" />
                <input type="text" placeholder="How you know them (optional)" value={r.relationship} autoComplete="off"
                  onChange={(e) => setRef(i, { relationship: e.target.value })} className="input h-11 text-base" />
                <input type="email" placeholder="Their email" value={r.email} autoComplete="off"
                  onChange={(e) => setRef(i, { email: e.target.value })} className="input h-11 text-base" />
                <input type="tel" placeholder="Their phone" value={r.phone} autoComplete="off"
                  onChange={(e) => setRef(i, { phone: e.target.value })} className="input h-11 text-base" />
              </div>
            </div>
          ))}
          {state.refs.length < 3 && (
            <button
              type="button"
              onClick={() => patch({ refs: [...state.refs, { ...EMPTY_REF }] })}
              className="text-sm text-navy-500 hover:text-gold-600 underline underline-offset-4 min-h-[44px]"
              data-testid="signup-ref-add"
            >
              + Add another reference
            </button>
          )}
          <p className="text-xs text-navy-300">Each reference needs an email or phone so our team can reach them.</p>
        </div>
      )}
    </div>
  )
}

// ─── Step: supplier stock connect ───────────────────────────────────────────

function StepStock({ draft, onChange, busy, onSkip }: {
  draft: DataSourceDraft
  onChange: (d: DataSourceDraft) => void
  busy: boolean
  onSkip: () => void
}) {
  return (
    <div data-testid="signup-stock">
      <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
        How does your stock reach us?
      </h1>
      <p className="mt-2 text-sm text-navy-500">
        The easiest option is to send us your API documentation — we build the connection for you.
        You can do this any time from My Stock, and we ask suppliers to have it done within 7 days
        of signing up.
      </p>
      <div className="mt-8">
        <DataSourceSetup draft={draft} onChange={onChange} />
      </div>
      <button
        type="button"
        onClick={onSkip}
        disabled={busy}
        className="mt-4 text-sm text-navy-300 hover:text-gold-600 underline underline-offset-4 min-h-[44px]"
        data-testid="signup-stock-skip"
      >
        I’ll do this later — set it up any time from My Stock.
      </button>
    </div>
  )
}

// ─── Step: done ─────────────────────────────────────────────────────────────

/**
 * `needsDocs` is the OTHER half of removing the company-documents block. A
 * user who finished with nothing filed must not be told "we're verifying your
 * account" — nothing has been submitted and nobody is reviewing anything. A
 * promise we cannot keep is a worse dead end than the block was. They get the
 * truth, and the control that fixes it: `/kyc`, which exists on both shells
 * and drives the same signed-URL upload.
 */
function StepDone({ name, onDone, needsDocs, onAddDocs, reduce }: {
  name: string
  onDone: () => void
  needsDocs: boolean
  onAddDocs: () => void
  reduce: boolean
}) {
  const first = name.trim().split(/\s+/)[0] || 'there'
  return (
    <div className="text-center py-6" data-testid="signup-done">
      <motion.svg
        viewBox="0 0 64 64" width="88" height="88" fill="none" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round"
        className="mx-auto text-ink"
        initial={reduce ? undefined : { opacity: 0, scale: 0.85 }}
        animate={reduce ? undefined : { opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        aria-hidden
      >
        <motion.path
          d="M18 10h28l10 14-24 30L8 24l10-14Z" className="stroke-current"
          initial={reduce ? undefined : { pathLength: 0 }}
          animate={reduce ? undefined : { pathLength: 1 }}
          transition={{ duration: 0.9, ease: 'easeInOut' }}
        />
        <motion.path
          d="M8 24h48" className="stroke-gold-500"
          initial={reduce ? undefined : { pathLength: 0 }}
          animate={reduce ? undefined : { pathLength: 1 }}
          transition={{ duration: 0.7, delay: 0.5, ease: 'easeInOut' }}
        />
        <motion.path
          d="M18 10l7 14 7 30 7-30 7-14" className="stroke-current opacity-60"
          initial={reduce ? undefined : { pathLength: 0 }}
          animate={reduce ? undefined : { pathLength: 1 }}
          transition={{ duration: 0.9, delay: 0.35, ease: 'easeInOut' }}
        />
      </motion.svg>
      <motion.div
        initial={reduce ? undefined : { opacity: 0, y: 12 }}
        animate={reduce ? undefined : { opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        <h1 className="mt-6 font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1">
          You're in, {first}.
        </h1>
        <p className="mt-3 text-sm text-navy-500 max-w-md mx-auto leading-relaxed">
          {needsDocs ? (
            <>
              Your account is live — browse the full catalog now. We can't unlock prices or
              supplier contact until one company document lands, and it usually takes
              a day once it does.
            </>
          ) : (
            <>
              We're verifying your account — please allow <strong className="text-ink">1–2 business days</strong>.
              Browse the full catalog now; prices and supplier contact unlock the moment you're approved.
              We'll email you.
            </>
          )}
        </p>
        <div className="mt-8 flex flex-col items-center gap-3">
          <button type="button" onClick={onDone} className="btn-primary h-[52px] px-8 text-base" data-testid="signup-browse">
            Browse the catalog
          </button>
          {needsDocs && (
            <PressScale
              feel="tap"
              onClick={onAddDocs}
              className="btn-ghost h-11 px-4 text-sm underline underline-offset-4"
              data-testid="signup-done-add-docs"
            >
              Add your documents now →
            </PressScale>
          )}
        </div>
      </motion.div>
    </div>
  )
}
